// CD: components/feedback/Toast.jsx — CD: backend
// CD: components/mobile-drawer/MobileDrawer.jsx (overlay behaviour only: Escape, focus, scroll lock; no markup)
// CD: components/pdp-extras/SizeChartModal.jsx (same behaviour; markup comes from the parity-tested snippets)
//
// pushe-ui.js: the one module shared by every page. Every other pushe-*.js imports from here.
//
//   events     EVENTS, emit, on
//   routes     routesRoot
//   sections   fetchSectionDocument, fetchSectionsHtml   (Section Rendering API)
//   toast      showToast                                 (clones a <template> rendered by a snippet; `vars` fills {key})
//   motion     prefersReducedMotion
//   elements   defineOnce, PusheDialog                   (<pushe-dialog>, registered below)
//
// One instance. Every module imports this file RELATIVELY, never through a bare specifier and never through an
// absolute URL:
//     import { emit, showToast } from './pushe-ui.js';
// The browser resolves that to the URL without ?v=, while layout/theme.liquid loads the versioned one. The import
// map in snippets/pushe-head-assets.liquid sends the first URL to the second, so the file is evaluated once and the
// state below (toast handles, the toast listener, the open dialog) is shared. A browser without import maps (iOS
// below 16.4) loads two instances and keeps working: that is why a bare specifier, which would fail to resolve there
// and leave the page without any JS, is forbidden.
//
// Rules for this file:
//   - window, document, fetch, customElements and matchMedia are read when a function RUNS, never stored at the
//     top level: the unit tests swap those globals between cases. The only top-level reads are the two guarded
//     registrations at the end of the file, which keep no reference.
//   - No UI markup is built from strings. Toasts clone a <template>; dialogs wrap markup rendered by Liquid.
//   - No text is hard-coded. Messages arrive from callers (from locale files) or from the cloned template. A value that
//     belongs inside a localized sentence (the product name in "added to your bag") arrives as showToast `vars`: the
//     template keeps a literal {name} and the toast swaps it in the TEXT NODES of its body, so the template's markup and
//     attributes (the cart link and its href) are never touched and no HTML is ever parsed.

/** Event names shared across modules. Payloads: CART_UPDATED { item_count }, TOAST { message, tone, templateId, vars }. */
export const EVENTS = Object.freeze({ CART_UPDATED: 'pushe:cart:updated', TOAST: 'pushe:toast' });

const DIALOG_OPEN = 'pushe:dialog:open';
const DIALOG_CLOSE = 'pushe:dialog:close';

// ------------------------------------------------------------------------------------------------
// Events, routes, motion, custom elements
// ------------------------------------------------------------------------------------------------

/** Dispatch a bubbling CustomEvent on document. */
export function emit(name, detail) {
  bridgeToastRequests(document);
  document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
}

/** Listen on document. Returns the function that stops listening (on the same document it started on). */
export function on(name, handler) {
  const target = document;
  target.addEventListener(name, handler);
  return () => target.removeEventListener(name, handler);
}

/** Root of the storefront routes of the current language, always ending in a slash (for example '/' or '/en/'). */
export function routesRoot() {
  return window.Shopify?.routes?.root ?? '/';
}

export function prefersReducedMotion() {
  return typeof matchMedia === 'function' && Boolean(matchMedia('(prefers-reduced-motion: reduce)').matches);
}

const inputDocuments = new WeakSet();
export function trackInputModality(doc = document) {
  if (inputDocuments.has(doc)) return;
  inputDocuments.add(doc);
  doc.addEventListener('keydown', () => { doc.documentElement.dataset.pusheInput = 'keyboard'; }, true);
  doc.addEventListener('pointerdown', (event) => {
    doc.documentElement.dataset.pusheInput = 'pointer';
    const marquee = event.target.closest?.('.pushe-marquee--pause');
    if (marquee && event.pointerType !== 'mouse') marquee.focus({ preventScroll: true });
    else if (!marquee && doc.activeElement?.matches?.('.pushe-marquee--pause')) doc.activeElement.blur();
  }, true);
}

export function keyboardInteraction(event) {
  trackInputModality();
  return event?.type === 'click' ? event.detail === 0 : document.documentElement.dataset.pusheInput === 'keyboard';
}

/**
 * Register a custom element once. The theme editor re-renders sections and re-runs module code paths; a second
 * define() would throw. Returns the class that is registered under the tag (the first one wins).
 */
export function defineOnce(tagName, ctor) {
  if (!customElements.get(tagName)) customElements.define(tagName, ctor);
  return customElements.get(tagName);
}

// ------------------------------------------------------------------------------------------------
// Section Rendering API
// ------------------------------------------------------------------------------------------------

const SECTIONS_PER_REQUEST = 5;

// The fragment never reaches the server, and a query string placed after it would be lost.
function withParam(url, key, value) {
  const text = String(url);
  const hash = text.indexOf('#');
  const base = hash === -1 ? text : text.slice(0, hash);
  return `${base}${base.includes('?') ? '&' : '?'}${key}=${value}`;
}

function parseHtml(html) {
  const Parser = typeof DOMParser === 'function' ? DOMParser : window.DOMParser;
  return new Parser().parseFromString(html, 'text/html');
}

/** Fetch one rendered section of `url` (?section_id=). Resolves to a parsed Document; rejects on non-2xx. */
export async function fetchSectionDocument(sectionId, url, { signal } = {}) {
  const response = await fetch(withParam(url, 'section_id', encodeURIComponent(sectionId)), { signal });
  if (!response.ok) throw new Error(`section_fetch_failed:${response.status}`);
  return parseHtml(await response.text());
}

/**
 * Fetch up to five rendered sections of `url` in one request (?sections=). Resolves to { [id]: html | null },
 * with an entry for every requested id. The API refuses more than five.
 */
export async function fetchSectionsHtml(ids, url, { signal } = {}) {
  if (ids.length > SECTIONS_PER_REQUEST) throw new RangeError(`section_ids_limit:${ids.length}`);
  if (ids.length === 0) return {};
  const response = await fetch(withParam(url, 'sections', ids.map(encodeURIComponent).join(',')), { signal });
  if (!response.ok) throw new Error(`section_fetch_failed:${response.status}`);
  const payload = await response.json();
  return Object.fromEntries(ids.map((id) => [id, typeof payload?.[id] === 'string' ? payload[id] : null]));
}

// ------------------------------------------------------------------------------------------------
// Toast (behaviour of components/feedback/Toast.jsx; markup comes from the pushe-toast snippet)
// ------------------------------------------------------------------------------------------------

const toastHandles = new WeakMap(); // toast element -> { close }
const bridgedDocuments = new WeakSet();

function computedStyleOf(el) {
  return typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el) : null;
}

// Duration of a CSS custom property in ms ('4s' or '150ms'), or `fallback` when it is missing.
function cssMs(name, fallback) {
  const style = computedStyleOf(document.documentElement);
  const raw = style ? String(style.getPropertyValue(name) ?? '').trim() : '';
  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return fallback;
  return raw.endsWith('ms') ? value : value * 1000;
}

function toastHost() {
  let host = document.querySelector('[data-pushe-toasts]');
  if (!host) {
    host = document.createElement('div');
    host.setAttribute('data-pushe-toasts', '');
    document.body.append(host);
  }
  return host;
}

// Lets any module ask for a toast with emit(EVENTS.TOAST, { message, tone, templateId, vars }). Attached lazily, once
// per document, so it also works for a document that was swapped in after this module was evaluated.
function bridgeToastRequests(doc) {
  if (bridgedDocuments.has(doc)) return;
  bridgedDocuments.add(doc);
  doc.addEventListener(EVENTS.TOAST, (event) => { showToast(event.detail ?? {}); });
}

// {key} placeholders: the product name inside the localized "added to your bag" sentence. Only TEXT NODES are visited, so the
// template's markup and attributes (the cart link, its href) are never touched and no HTML is parsed: a value always lands as
// text. One pass per node with a replacer function, so a value that itself holds "{other}" or "$&" is inserted as written.
// A key that `vars` does not hold (or holds as null or undefined, or only through the prototype) stays as written.
const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const PLACEHOLDER = /\{([^{}]+)\}/g;

function fillPlaceholders(root, vars) {
  const hasValue = (key) => Object.prototype.hasOwnProperty.call(vars, key) && vars[key] != null;
  for (let node = root.firstChild; node; node = node.nextSibling) {
    if (node.nodeType === TEXT_NODE) node.nodeValue = node.nodeValue.replace(PLACEHOLDER, (whole, key) => (hasValue(key) ? String(vars[key]) : whole));
    else if (node.nodeType === ELEMENT_NODE) fillPlaceholders(node, vars);
  }
}

/**
 * Show a toast. `templateId` names a <template> holding the toast markup. A `message` replaces the text of
 * .pushe-toast__body (textContent, never HTML); without one the template body is shown as rendered (the
 * "added to bag" template carries its own text and link). `vars` ({ name: 'flower top' }) then fills every {key} in the
 * text nodes of that body with String(vars[key]), as text; markup and attributes stay as rendered and an unknown key
 * stays as written. tone 'error' never auto-hides.
 * Only one toast at a time: a new one closes the previous. Returns { close() }.
 */
export function showToast({ templateId = 'pushe-toast-template', message, tone = 'success', vars } = {}) {
  const source = document.getElementById(templateId)?.content?.firstElementChild;
  if (!source) {
    console.warn(`pushe_toast_template_missing:${templateId}`);
    return { close() {} };
  }
  const el = source.cloneNode(true);
  if (tone === 'error') {
    el.classList.remove('pushe-toast--success');
    el.classList.add('pushe-toast--error');
    el.setAttribute('role', 'alert');
  }
  const body = el.querySelector('.pushe-toast__body');
  if (body && message != null && message !== '') body.textContent = String(message);
  if (body && vars && typeof vars === 'object') fillPlaceholders(body, vars);

  // CD: components/feedback/Toast.jsx — owner mobile exception, docs/claude-design-gui-lai-2026-09-24.md:168.
  const mobileAdded = templateId === 'pushe-toast-added-template' && typeof matchMedia === 'function' && matchMedia('(max-width: 899px)').matches;
  const autoHide = tone !== 'error' && !mobileAdded;
  let timer = null;
  let hovered = false;
  let focused = false;
  let leaving = false;
  let exitTimer = null;

  const disarm = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const arm = () => {
    disarm();
    if (!autoHide || leaving || hovered || focused) return;
    timer = setTimeout(close, cssMs('--pushe-hold-toast', 4000));
  };
  const remove = () => {
    leaving = true;
    disarm();
    clearTimeout(exitTimer);
    el.removeEventListener('transitionend', ended);
    el.removeEventListener('transitioncancel', cancelled);
    el.remove();
  };
  function ended(event) {
    if (leaving && event.target === el && event.propertyName === 'opacity') remove();
  }
  function cancelled(event) {
    // Retargeting entry to exit cancels the entry transition; the new exit still owns removal.
    if (leaving && event.target === el && event.propertyName === 'opacity' && !el.getAnimations?.().some(animation => animation.playState === 'running')) remove();
  }
  function close() {
    if (leaving) return;
    leaving = true;
    disarm();
    el.classList.add('is-leaving');
    exitTimer = setTimeout(remove, cssMs('--pushe-dur-exit', 150));
  }

  el.addEventListener('transitionend', ended);
  el.addEventListener('transitioncancel', cancelled);

  el.addEventListener('mouseenter', () => { hovered = true; disarm(); });
  el.addEventListener('mouseleave', () => { hovered = false; arm(); });
  el.addEventListener('focusin', () => { focused = true; disarm(); });
  el.addEventListener('focusout', (event) => {
    if (event.relatedTarget && el.contains(event.relatedTarget)) return;
    focused = false;
    arm();
  });
  el.querySelector('.pushe-toast__close')?.addEventListener('click', close);

  const handle = { close };
  toastHandles.set(el, { close, remove });
  const host = toastHost();
  // A replacement is immediate: cancel the old lifecycle so two fixed banners never paint over each other.
  for (const previous of host.querySelectorAll('.pushe-toast')) toastHandles.get(previous)?.remove();
  el.classList.add('is-entering');
  host.append(el);
  computedStyleOf(el)?.opacity; // Establish the entry state before the transition retargets it.
  el.classList.remove('is-entering');
  arm();
  return handle;
}

// ------------------------------------------------------------------------------------------------
// <pushe-dialog>: behaviour for overlays whose markup comes from a snippet
// (components/mobile-drawer/MobileDrawer.jsx, components/pdp-extras/SizeChartModal.jsx)
// ------------------------------------------------------------------------------------------------

const FOCUSABLE = 'a[href],button,input,select,textarea,summary,[tabindex]';
const NOT_RENDERED = new Set(['script', 'style', 'link', 'meta', 'template', 'noscript']);
const openDialogs = new WeakMap(); // document -> the dialog that is open in it (one at a time)

const hasClassSuffix = (el, ...suffixes) => Array.from(el.classList).some((name) => suffixes.some((suffix) => name.endsWith(suffix)));

// The closer at or above `target`, below `boundary`: walk up until an element has a class token that ends in
// __close or __scrim. A child with its own look-alike class (pushe-chart__close-icon inside the close button) is
// passed over, not mistaken for the answer; an unclassed svg path inside a closer reaches the closer.
function closerAbove(target, boundary) {
  for (let node = target; node && node !== boundary; node = node.parentElement) {
    if (node.classList && hasClassSuffix(node, '__close', '__scrim')) return node;
  }
  return null;
}

// Siblings that must stay reachable while a dialog is open: things that are not rendered, and the toasts.
const staysInteractive = (el) => NOT_RENDERED.has(el.localName) || el.hasAttribute('data-pushe-toasts');

function focusableIn(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.hasAttribute('disabled') || el.getAttribute('tabindex') === '-1') return false;
    if (el.localName === 'input' && el.getAttribute('type') === 'hidden') return false;
    if (el.closest('[hidden],[inert]')) return false;
    // Closed details can still report child rectangles; only their first summary remains reachable.
    for (let ancestor = el; ancestor; ancestor = ancestor.parentElement) {
      const style = computedStyleOf(ancestor);
      if (style && (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility))) return false;
      if (ancestor.localName === 'details' && !ancestor.hasAttribute('open')) {
        const summary = Array.from(ancestor.children).find(child => child.localName === 'summary');
        if (!summary?.contains(el)) return false;
      }
    }
    return typeof el.getClientRects !== 'function' || el.getClientRects().length > 0;
  });
}

function focusPanel(panel) {
  if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
  panel.focus({ preventScroll: true });
}

// Same as the JSX: the close button takes focus when the dialog opens; fall back to the first stop, then the panel.
function focusInto(panel) {
  const stops = focusableIn(panel);
  const closer = stops.find((button) => button.localName === 'button' && hasClassSuffix(button, '__close'));
  const target = closer ?? stops[0];
  if (target) target.focus({ preventScroll: true });
  else focusPanel(panel);
}

/**
 * <pushe-dialog> wraps ONE panel: the snippet's [role="dialog"] element, rendered with the `hidden` attribute.
 * Without such a descendant the element itself is the panel.
 *
 *   open(triggerEl, { adopt })  show the panel, lock scroll, make the rest of the page inert, trap Tab,
 *                    focus the close button (or keep valid focus and mark an adopted fallback). Only one dialog
 *                    is open per document: opening another closes the first.
 *   close()          undo all of it and give focus back to triggerEl (or to the element focused before).
 *
 * Closers need no wiring: a click on any element whose class ends in __close or __scrim closes the dialog
 * (the classes the snippets already carry, such as pushe-chart__close and pushe-drawer__close). Escape closes.
 * Dispatches bubbling 'pushe:dialog:open' and 'pushe:dialog:close' on the element so a trigger can keep
 * aria-expanded in sync. A dialog that leaves the page while open (section re-render) cleans up after itself.
 */
export class PusheDialog extends HTMLElement {
  #open = false;
  #trigger = null;
  #inerted = [];
  #overflowBeforeLock = null;

  #onClick = (event) => {
    if (closerAbove(event.target, this)) this.close();
  };

  // A key that something else already used (an inner widget closing its own popup on Escape, a handler that took
  // Tab) is not ours. Ctrl, Alt and Meta with Tab switch browser tabs and windows: never trap those.
  #onKeydown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') this.close();
    else if (event.key === 'Tab' && !event.ctrlKey && !event.altKey && !event.metaKey) this.#trapFocus(event);
  };

  connectedCallback() {
    trackInputModality(this.ownerDocument);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    if (this.#open) this.#teardown(false);
  }

  get isOpen() {
    return this.#open;
  }

  get #panel() {
    return this.querySelector('[role="dialog"]') ?? this;
  }

  open(triggerEl, { adopt = false } = {}) {
    if (this.#open) return;
    const doc = this.ownerDocument;
    openDialogs.get(doc)?.#teardown(false);
    const active = doc.activeElement;
    this.#trigger = triggerEl ?? (active && active !== doc.body ? active : null);
    const panel = this.#panel;
    if (adopt) this.setAttribute('data-pushe-dialog-adopted', '');
    panel.removeAttribute('hidden');
    this.#open = true;
    openDialogs.set(doc, this);
    this.#lockScroll(panel);
    this.#silenceRestOfPage();
    doc.addEventListener('keydown', this.#onKeydown);
    if (!adopt || !focusableIn(panel).includes(active)) focusInto(panel);
    this.dispatchEvent(new CustomEvent(DIALOG_OPEN, { bubbles: true }));
  }

  close() {
    if (this.#open) this.#teardown(true);
  }

  #teardown(restoreFocus) {
    const doc = this.ownerDocument;
    this.#open = false;
    this.#panel.setAttribute('hidden', '');
    this.removeAttribute('data-pushe-dialog-adopted');
    doc.removeEventListener('keydown', this.#onKeydown);
    if (this.#overflowBeforeLock !== null) doc.body.style.overflow = this.#overflowBeforeLock;
    this.#overflowBeforeLock = null;
    for (const el of this.#inerted) el.removeAttribute('inert');
    this.#inerted = [];
    if (openDialogs.get(doc) === this) openDialogs.delete(doc);
    const trigger = this.#trigger;
    this.#trigger = null;
    if (restoreFocus && trigger) {
      // A trigger that left the page (its section was re-rendered while the dialog was open) cannot take focus:
      // fall back to the main landmark, which carries tabindex -1, instead of leaving focus to the browser.
      const target = trigger.isConnected && typeof trigger.focus === 'function' ? trigger : doc.getElementById('MainContent');
      target?.focus({ preventScroll: true });
    }
    this.dispatchEvent(new CustomEvent(DIALOG_CLOSE, { bubbles: true }));
  }

  // Lock the page behind the dialog only when the panel really covers the viewport (position:fixed), as the JSX does.
  #lockScroll(panel) {
    const style = computedStyleOf(panel);
    if (style && style.position !== 'fixed') return;
    const body = this.ownerDocument.body;
    this.#overflowBeforeLock = body.style.overflow;
    body.style.overflow = 'hidden';
  }

  // Everything outside the dialog goes inert: walk up from this element and mark the siblings at every level.
  // The ancestors stay live (they contain the dialog), and so do the toasts. Only inert that we add is removed.
  #silenceRestOfPage() {
    const body = this.ownerDocument.body;
    for (let node = this; node.parentElement && node !== body; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.hasAttribute('inert') || staysInteractive(sibling)) continue;
        sibling.setAttribute('inert', '');
        this.#inerted.push(sibling);
      }
    }
  }

  // Tab leaves the last stop (Shift+Tab the first): wrap to the other end. Focus outside the panel is pulled back in.
  #trapFocus(event) {
    const panel = this.#panel;
    const stops = focusableIn(panel);
    if (stops.length === 0) {
      event.preventDefault();
      focusPanel(panel);
      return;
    }
    const active = this.ownerDocument.activeElement;
    const inside = stops.includes(active);
    const first = stops[0];
    const last = stops[stops.length - 1];
    const leavingEdge = event.shiftKey ? first : last;
    if (!inside || active === leavingEdge) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    }
  }
}

if (typeof customElements !== 'undefined') defineOnce('pushe-dialog', PusheDialog);
if (typeof document !== 'undefined') { bridgeToastRequests(document); trackInputModality(document); }
